import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const PLAYER_IMG =
  "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?q=85&w=1200&auto=format&fit=crop";
const SCOUT_IMG =
  "https://images.unsplash.com/photo-1676746610993-fa0c050d1f6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxzb2NjZXIlMjBzdGFkaXVtJTIwZmxvb2RsaWdodHMlMjBuaWdodCUyMGF0bW9zcGhlcmV8ZW58MHx8fHwxNzg4NzE5NTc4fDA&ixlib=rb-4.1.0&q=85&w=1200&auto=format&fit=crop";

export default function Audiences({ t, audience, setAudience }) {
  const isPlayers = audience === "players";
  const data = isPlayers
    ? {
        title: t.audiences.playersTitle,
        body: t.audiences.playersBody,
        benefits: t.audiences.playersBenefits,
        img: PLAYER_IMG,
      }
    : {
        title: t.audiences.scoutsTitle,
        body: t.audiences.scoutsBody,
        benefits: t.audiences.scoutsBenefits,
        img: SCOUT_IMG,
      };

  return (
    <section
      id="audiences"
      data-testid="audiences-section"
      className="relative border-y border-white/[0.06] bg-[#0A0D14] py-28 lg:py-36"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#00F562]">
          {t.audiences.overline}
        </p>
        <h2 className="mt-4 max-w-3xl font-display text-2xl font-extrabold uppercase tracking-tight sm:text-3xl lg:text-4xl">
          {t.audiences.title}
        </h2>

        <div
          data-testid="audience-tabs"
          className="mt-12 inline-flex rounded-full border border-white/10 bg-[#06080C] p-1.5"
        >
          {[
            { id: "players", label: t.audiences.tabPlayers },
            { id: "scouts", label: t.audiences.tabScouts },
          ].map((tab) => (
            <button
              key={tab.id}
              data-testid={`audience-tab-${tab.id}`}
              onClick={() => setAudience(tab.id)}
              className={`relative rounded-full px-6 py-3 text-xs font-bold uppercase tracking-widest transition-colors duration-300 ${
                audience === tab.id ? "text-[#06080C]" : "text-slate-400 hover:text-white"
              }`}
            >
              {audience === tab.id && (
                <motion.span
                  layoutId="audience-pill"
                  className="absolute inset-0 rounded-full bg-[#00F562]"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                />
              )}
              <span className="relative z-10">{tab.label}</span>
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={audience}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.55, ease: EASE }}
            className="mt-12 grid grid-cols-1 items-center gap-12 lg:grid-cols-2"
          >
            <div>
              <h3 className="font-display text-xl font-bold tracking-tight sm:text-2xl">
                {data.title}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-slate-400">
                {data.body}
              </p>
              <ul className="mt-8 space-y-4">
                {data.benefits.map((b, i) => (
                  <motion.li
                    key={b}
                    data-testid={`audience-benefit-${i}`}
                    initial={{ opacity: 0, x: -16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 + i * 0.08 }}
                    className="flex items-start gap-3 text-sm text-slate-200"
                  >
                    <CheckCircle2
                      size={18}
                      className="mt-0.5 shrink-0 text-[#00F562]"
                    />
                    {b}
                  </motion.li>
                ))}
              </ul>
            </div>
            <motion.div
              whileHover={{ scale: 1.015 }}
              transition={{ duration: 0.5 }}
              className="relative overflow-hidden rounded-2xl border border-white/[0.08]"
            >
              <img
                data-testid={`audience-image-${audience}`}
                src={data.img}
                alt={data.title}
                className="aspect-[4/3] w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#06080C]/80 via-transparent to-transparent" />
              <div className="absolute left-1/2 top-0 h-full w-1/2 -translate-x-1/2 bg-[#00F562]/[0.04] blur-3xl" />
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
